import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getServerUrl } from '../utils/environment';

// Recommended minimum speeds in Mbps for a smooth interview experience.
const RECOMMENDED_MIN_DOWNLOAD_SPEED_MBPS = 4;
const RECOMMENDED_MIN_UPLOAD_SPEED_MBPS = 4;

const TEST_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const DOWNLOAD_TEST_URL = `${getServerUrl()}/speed-test/download`;
const UPLOAD_TEST_URL = `${getServerUrl()}/speed-test/upload`;

type SpeedTestStatus = 'testing' | 'completed' | 'error';
export type SpeedTestVerdict = 'Good' | 'Fair' | 'Poor';

export const useInternetSpeedCheck = () => {
  const [downloadStatus, setDownloadStatus] =
    useState<SpeedTestStatus>('testing');
  const [uploadStatus, setUploadStatus] = useState<SpeedTestStatus>('testing');
  const [downloadSpeedMbps, setDownloadSpeedMbps] = useState<number | null>(
    null,
  );
  const [uploadSpeedMbps, setUploadSpeedMbps] = useState<number | null>(null);
  const [isDownloadTooSlow, setIsDownloadTooSlow] = useState<boolean>(false);
  const [isUploadTooSlow, setIsUploadTooSlow] = useState<boolean>(false);
  const [testTrigger, setTestTrigger] = useState(0);

  const runSpeedTest = useCallback(() => {
    setTestTrigger((count) => count + 1);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: testTrigger intentionally retriggers the speed tests
  useEffect(() => {
    let isCancelled = false;

    const runTests = async () => {
      // Reset state for retry
      setDownloadStatus('testing');
      setUploadStatus('testing');
      setDownloadSpeedMbps(null);
      setUploadSpeedMbps(null);
      setIsDownloadTooSlow(false);
      setIsUploadTooSlow(false);

      const runDownloadTest = async () => {
        try {
          const startTime = Date.now();
          const response = await fetch(DOWNLOAD_TEST_URL, {
            cache: 'no-store',
          });

          if (!response.ok) {
            throw new Error(
              `Failed to fetch test file: ${response.statusText}`,
            );
          }

          await response.blob(); // Consume the body to complete the download
          const endTime = Date.now();
          const durationSeconds = (endTime - startTime) / 1000;

          if (isCancelled || durationSeconds === 0) return;

          const bitsLoaded = TEST_FILE_SIZE_BYTES * 8;
          const speedBps = bitsLoaded / durationSeconds;
          const speedMbps = speedBps / 1024 / 1024;

          if (!isCancelled) {
            setDownloadSpeedMbps(speedMbps);
            const slow = speedMbps < RECOMMENDED_MIN_DOWNLOAD_SPEED_MBPS;
            setIsDownloadTooSlow(slow);
            setDownloadStatus('completed');
            datadogRum.addAction('download_speed_test_completed', {
              download_speed_mbps: speedMbps.toFixed(2),
              is_too_slow: slow,
            });
          }
        } catch (error) {
          if (!isCancelled) {
            console.error('Download speed test failed:', error);
            setDownloadStatus('error');
            datadogRum.addError('download_speed_test_failed', {
              error: error instanceof Error ? error.message : String(error),
            });
          }
        }
      };

      const runUploadTest = async () => {
        try {
          const payload = new Blob([new ArrayBuffer(TEST_FILE_SIZE_BYTES)], {
            type: 'application/octet-stream',
          });
          const startTime = Date.now();
          const response = await fetch(UPLOAD_TEST_URL, {
            method: 'POST',
            body: payload,
          });

          if (!response.ok) {
            throw new Error(`Upload test failed: ${response.statusText}`);
          }

          const endTime = Date.now();
          const durationSeconds = (endTime - startTime) / 1000;

          if (isCancelled || durationSeconds === 0) return;

          const bitsLoaded = TEST_FILE_SIZE_BYTES * 8;
          const speedBps = bitsLoaded / durationSeconds;
          const speedMbps = speedBps / 1024 / 1024;

          if (!isCancelled) {
            setUploadSpeedMbps(speedMbps);
            const slow = speedMbps < RECOMMENDED_MIN_UPLOAD_SPEED_MBPS;
            setIsUploadTooSlow(slow);
            setUploadStatus('completed');
            datadogRum.addAction('upload_speed_test_completed', {
              upload_speed_mbps: speedMbps.toFixed(2),
              is_too_slow: slow,
            });
          }
        } catch (error) {
          if (!isCancelled) {
            console.error('Upload speed test failed:', error);
            setUploadStatus('error');
            datadogRum.addError('upload_speed_test_failed', {
              error: error instanceof Error ? error.message : String(error),
            });
          }
        }
      };

      // Run tests in parallel
      await Promise.all([runDownloadTest(), runUploadTest()]);
    };

    runTests();

    return () => {
      isCancelled = true;
    };
  }, [testTrigger]);

  const isTesting = downloadStatus === 'testing' || uploadStatus === 'testing';

  const verdict = useMemo((): SpeedTestVerdict => {
    if (isTesting) {
      // While testing, we can assume 'Good' until proven otherwise,
      // or handle a 'Testing' state in the UI.
      return 'Good';
    }
    const isGood = !isDownloadTooSlow && !isUploadTooSlow;
    if (isGood) return 'Good';

    const hasError = downloadStatus === 'error' || uploadStatus === 'error';
    if (hasError) return 'Poor';

    if (isDownloadTooSlow && isUploadTooSlow) return 'Poor';

    return 'Fair';
  }, [
    downloadStatus,
    uploadStatus,
    isDownloadTooSlow,
    isUploadTooSlow,
    isTesting,
  ]);

  useEffect(() => {
    if (!isTesting && verdict !== 'Good') {
      datadogRum.addAction('internet_speed_test_poor_result', {
        verdict,
        download_speed_mbps: downloadSpeedMbps?.toFixed(1),
        upload_speed_mbps: uploadSpeedMbps?.toFixed(1),
      });
    }
  }, [isTesting, verdict, downloadSpeedMbps, uploadSpeedMbps]);

  return {
    downloadStatus,
    uploadStatus,
    downloadSpeedMbps,
    uploadSpeedMbps,
    isDownloadTooSlow,
    isUploadTooSlow,
    isTesting,
    verdict,
    runSpeedTest,
  };
};
